import React from 'react';
import { useEntitySelection } from '@shared/api/providers/entitySelection';
import { View, ViewProps } from '@shared/components/View';
import { IsCardBeforePaidCutoffHandler } from '@shared/data/billing/hooks/useIsCardBeforePaidCutoff';
import { Entity } from '@shared/data/entity/Entity';
import { usePaywallExperiment } from '@shared/util/featureFlags/launchDarkly/hooks/usePaywallExperiment';
import { useDraggableContext } from '../../listView/components/DraggableContext';
import { OpenEntityHandler } from '../hooks/useCollectionViewData';
import DefaultCardStyles from './DefaultCardStyles.module.css';
import { CardOverflowButton } from './cardOptions';
import { EntitySelectionCheckbox } from './cardOptions/EntitySelectionCheckbox';
import { EntitySelectionOverlay } from './cardOptions/EntitySelectionOverlay';

interface Props extends ViewProps {
  workflowTemplate: MBAPI.WorkflowTemplate;
  entity: Entity;
  openEntity?: OpenEntityHandler;
  getIsCardBeforePaidCutoff: IsCardBeforePaidCutoffHandler;
  hasOverflowMenu?: boolean;
  isHovered?: boolean;
  cardStyles?: CollectionViewCardStyles;
}

export interface CollectionViewCardStyles {
  card?: string;
  content?: string;
  overflowButton?: string;
  overflowButtonHovered?: string;
  selectionCheckbox?: string;
}

export function CollectionViewCard({
  children,
  workflowTemplate,
  entity,
  openEntity,
  getIsCardBeforePaidCutoff,
  hasOverflowMenu = true,
  isHovered = false,
  cardStyles,
  ...viewProps
}: Props) {
  const { isRedactedGroup } = usePaywallExperiment();
  const isCardBeforePaidCutoff = getIsCardBeforePaidCutoff(entity);
  const isRedacted = isRedactedGroup && isCardBeforePaidCutoff;
  const { isDragging } = useDraggableContext();

  const { selectionActive, isEntitySelected } = useEntitySelection();
  const showSelection = selectionActive && !isRedacted && !isDragging;
  const isSelected = isEntitySelected(entity.id);
  const showOverflowMenu = hasOverflowMenu && !showSelection && !isRedacted && !isDragging;

  return (
    <View
      className={[
        DefaultCardStyles.card,
        cardStyles?.card,
        isRedacted && DefaultCardStyles.redacted,
        isSelected && showSelection && DefaultCardStyles.selected,
      ]}
      {...viewProps}
    >
      <View className={cardStyles?.content}>{children}</View>
      {showOverflowMenu && (
        <CardOverflowButton
          entity={entity}
          workflowTemplate={workflowTemplate}
          onPressOpenEntity={() => openEntity?.(entity.id, entity.navigationViewTemplateId)}
          className={[
            DefaultCardStyles.overflowButton,
            cardStyles?.overflowButton,
            isHovered && cardStyles?.overflowButtonHovered,
          ]}
        />
      )}
      {showSelection && (
        <EntitySelectionCheckbox
          isSelected={isSelected}
          className={[DefaultCardStyles.selectionCheckbox, cardStyles?.selectionCheckbox]}
        />
      )}
      {showSelection && <EntitySelectionOverlay entity={entity} />}
    </View>
  );
}
